import React, { useEffect, useMemo, useState } from "react";
import { Button, Progress, message } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import UseApi from "../../Reusable/CallApi/UseApi";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .back {
    cursor: pointer;
    color: #68728a;
  }

  .title {
    font-size: 20px;
    font-weight: 500;
    color: #1f243b;
  }

  .progress-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .body {
    display: flex;
    gap: 24px;
  }

  .units {
    width: 220px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .unit-item {
    padding: 12px 16px;
    border-radius: 8px;
    cursor: pointer;
    color: #68728a;
  }

  .unit-item.active {
    background: #f0f0f0;
    color: #1f243b;
    font-weight: 500;
  }

  .content {
    flex: 1;
    min-width: 0;
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .content-title {
    font-size: 16px;
    font-weight: 500;
    color: #1f243b;
  }

  .media-frame {
    width: 100%;
    height: 420px;
    border: none;
    border-radius: 8px;
    background: #f5f5f5;
  }

  .footer {
    display: flex;
    justify-content: flex-end;
  }

  /* Mint green, matching Figma's primary action color - same palette
     already used for Group Files' buttons (Reportpeoplegroup.js). */
  .mint-button {
    background: #c2fae5;
    border-color: #c2fae5;
    color: #1f243b;
  }
  .mint-button:hover,
  .mint-button:focus {
    background: #aef0d4 !important;
    border-color: #aef0d4 !important;
    color: #1f243b !important;
  }
`;

// The one piece nothing in this app had anywhere, learner or otherwise:
// an actual "take this course" view. Progress here is a single number on
// tymeplushrlmscourseenrollment (not per-unit), so "Complete & Next"
// advances it as (units seen so far / total units) - a reasonable
// approximation given the schema, not a claim that any real
// assessment/completion criteria was checked.
const CoursePlayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { courseId, enrollmentId } = location.state || {};
  const clientId = useSelector((state) => state.authTracking.userInfo.clientId);

  const { data, loading, request } = UseApi();
  const { request: updateRequest, loading: updating } = UseApi();
  const [activeUnitIndex, setActiveUnitIndex] = useState(0);

  useEffect(() => {
    if (!clientId || !courseId) return;
    request({ endPoint: `/course/getcoursebyid/${clientId}?course_id=${courseId}`, method: "get" }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, courseId]);

  const course = data?.response;
  const units = useMemo(() => (Array.isArray(course?.units) ? course.units : []), [course]);
  const activeUnit = units[activeUnitIndex];
  const progress = units.length ? Math.round(((activeUnitIndex + 1) / units.length) * 100) : 0;

  const handleCompleteAndNext = async () => {
    const isLast = activeUnitIndex >= units.length - 1;
    try {
      if (enrollmentId) {
        await updateRequest({
          endPoint: `/course/updateenrollment/${clientId}/${enrollmentId}`,
          method: "patch",
          data: {
            progress: isLast ? 100 : progress,
            status: isLast ? "completed" : "in_progress",
          },
        });
      }
      if (isLast) {
        message.success("Course completed!");
        navigate("/my-course");
      } else {
        setActiveUnitIndex((index) => index + 1);
      }
    } catch (error) {
      message.error("Failed to update progress");
    }
  };

  if (!courseId) {
    return (
      <Wrapper>
        <div className="title">Course</div>
        <div>No course selected. Go back to My Course and pick one to continue.</div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="back" onClick={() => navigate("/my-course")}>
        <LeftOutlined /> My Course
      </div>
      <div className="title">{course?.course_name || (loading ? "Loading..." : "Course")}</div>

      {units.length > 0 && (
        <div className="progress-row">
          <div>Progress</div>
          <Progress percent={progress} style={{ maxWidth: 300 }} />
        </div>
      )}

      <div className="body">
        <div className="units">
          {units.map((unit, index) => (
            <div
              key={unit.id || index}
              className={`unit-item ${index === activeUnitIndex ? "active" : ""}`}
              onClick={() => setActiveUnitIndex(index)}
            >
              {unit.title || unit.type || `Unit ${index + 1}`}
            </div>
          ))}
          {!loading && units.length === 0 && <div>This course has no units yet.</div>}
        </div>

        {activeUnit && (
          <div className="content">
            <div className="content-title">{activeUnit.title || activeUnit.type}</div>

            {activeUnit.mediaUrl && ["Video", "Audio"].includes(activeUnit.type) && (
              <video className="media-frame" src={activeUnit.mediaUrl} controls />
            )}
            {activeUnit.mediaUrl && !["Video", "Audio"].includes(activeUnit.type) && (
              <iframe className="media-frame" src={activeUnit.mediaUrl} title={activeUnit.title} />
            )}
            {activeUnit.body && <div>{activeUnit.body}</div>}
            {!activeUnit.mediaUrl && !activeUnit.body && (
              <div>No content was added to this unit.</div>
            )}

            <div className="footer">
              <Button className="mint-button" loading={updating} onClick={handleCompleteAndNext}>
                {activeUnitIndex >= units.length - 1 ? "Complete Course" : "Complete & Next"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default CoursePlayer;
